import type { ShoppingCart } from '../../api/types/shoppingCart';
import type { AirBooking, AirPassengerBreakdown, FlightSegment } from '../../api/types/air';
import type { Passenger } from '../../api/types/common';
import { airlineCodeOf, airlineNameOf } from '../AirlineLogo';

/**
 * `Document/Eticket` sunucu HTML'i sade bir tablo. Sayfaya zaten gelen `GetShoppingCart`
 * verisi elimizdeyken önizleme/yazdırma için daha zengin bir e-bilet üretiyoruz: havayolu
 * logosu, kalkış/varış şehir + saat, süre, kabin/sınıf, PNR/bilet no ve (istenirse) ücret
 * kırılımı. Kendi kendine yeten tek HTML belgesi (inline CSS, `@media print`).
 *
 * Logolar `public/img/airlines/<KOD>.png` — yeni sekme `about:blank` origin'i devraldığı
 * için mutlak URL (`location.origin`) kullanılıyor.
 */
export type EticketOptions = {
  /** Yalnız bu PNR'ın (AirBooking) bileti — verilmezse/`grouped` ise tüm sepet. */
  pnr?: string;
  /** Yalnız bu yolcu (paxReferanceId). */
  paxReferenceId?: string;
  /** Fiyat/ücret bilgisi olmadan. */
  noPrice?: boolean;
  /** Sepetteki tüm biletler tek belgede. */
  grouped?: boolean;
};

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (v: unknown): string => String(v ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

function fmtDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? esc(iso)
    : d.toLocaleDateString('tr-TR', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
}
function fmtTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}
function money(n?: number, currency = 'TRY'): string {
  return `${(n ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

const CABIN_LABEL: Record<string, string> = { '1': 'First', '2': 'Business', '3': 'Premium Economy', '4': 'Economy' };
const cabin = (c?: string | number): string => (c == null ? '' : CABIN_LABEL[String(c)] ?? String(c));

function segmentRow(segment: FlightSegment, logoBase: string, noPrice: boolean): string {
  const code = airlineCodeOf(segment) ?? '';
  const name = airlineNameOf(segment) ?? code;
  const logo = code
    ? `<img class="logo" src="${logoBase}/${esc(code)}.png" alt="${esc(name)}" onerror="this.remove()"/>`
    : '';
  const dur = segment.journeyDuration ? `<span class="dur">${esc(segment.journeyDuration)}</span>` : '';
  const tags = [cabin(segment.cabinType), segment.bookingClass ? `Sınıf ${esc(segment.bookingClass)}` : '']
    .filter(Boolean)
    .map((t) => `<span class="tag">${esc(t)}</span>`)
    .join('');
  void noPrice;
  return `
    <div class="seg">
      <div class="seg-air">${logo}<div><div class="al-name">${esc(name)}</div><div class="al-fn">${esc(
        segment.flightNumber
      )}</div></div></div>
      <div class="seg-leg">
        <div class="ep">
          <div class="time">${fmtTime(segment.departureDateTime)}</div>
          <div class="code">${esc(segment.departureAirportCode)}</div>
          <div class="city">${esc(segment.departureCityName || segment.departureAirportName)}</div>
        </div>
        <div class="mid"><div class="line"></div>${dur}<div class="plane">✈</div></div>
        <div class="ep">
          <div class="time">${fmtTime(segment.arrivalDateTime)}</div>
          <div class="code">${esc(segment.arrivalAirportCode)}</div>
          <div class="city">${esc(segment.arrivalCityName || segment.arrivalAirportName)}</div>
        </div>
      </div>
      <div class="seg-meta">
        <div class="date">${fmtDate(segment.departureDateTime)}</div>
        <div class="tags">${tags}</div>
      </div>
    </div>`;
}

function paxTable(
  breakdowns: AirPassengerBreakdown[],
  paxByRef: Map<string, Passenger>,
  noPrice: boolean,
  currency: string
): string {
  if (breakdowns.length === 0) return '';
  const head = noPrice
    ? '<tr><th>Yolcu</th><th>Bilet No</th><th>Bagaj</th></tr>'
    : '<tr><th>Yolcu</th><th>Bilet No</th><th>Bagaj</th><th class="r">Ücret</th></tr>';
  const rows = breakdowns
    .map((pb) => {
      const p = pb.paxReference?.paxReferanceId ? paxByRef.get(pb.paxReference.paxReferanceId) : undefined;
      const nm = p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : pb.paxReference?.paxCode ?? '';
      const bag = (pb as { baggageAllowance?: string }).baggageAllowance ?? '';
      const fare = noPrice ? '' : `<td class="r">${money(pb.totalFare, currency)}</td>`;
      return `<tr><td>${esc(nm)}</td><td>${esc(pb.ticketNumber)}</td><td>${esc(bag)}</td>${fare}</tr>`;
    })
    .join('');
  return `<table class="pax">${head}${rows}</table>`;
}

function totalsBlock(booking: AirBooking, currency: string): string {
  const rows: [string, number | undefined][] = [
    ['Baz ücret', booking.baseFare],
    ['Vergi ve harçlar', booking.totalTax],
    ['Servis bedeli', booking.totalServiceFee],
  ];
  const body = rows
    .filter(([, v]) => v != null && v !== 0)
    .map(([k, v]) => `<div class="tr"><span>${k}</span><span>${money(v, currency)}</span></div>`)
    .join('');
  return `<div class="totals">${body}<div class="tr grand"><span>Toplam</span><span>${money(
    booking.totalFare,
    currency
  )}</span></div></div>`;
}

function bookingCard(
  booking: AirBooking,
  paxByRef: Map<string, Passenger>,
  logoBase: string,
  opts: EticketOptions
): string {
  const noPrice = !!opts.noPrice;
  const currency = typeof booking.currency === 'string' ? booking.currency : 'TRY';
  let breakdowns = booking.passengerBreakdowns ?? [];
  if (opts.paxReferenceId) {
    breakdowns = breakdowns.filter((pb) => pb.paxReference?.paxReferanceId === opts.paxReferenceId);
  }
  const segs = booking.flightSegments ?? [];
  const chips = [booking.provider, booking.status].filter(Boolean).map((c) => `<span class="chip">${esc(c)}</span>`).join('');
  return `
    <section class="card">
      <header class="card-h">
        <div><span class="pnr-l">PNR</span><span class="pnr">${esc(booking.bookingCode)}</span></div>
        <div class="chips">${chips}</div>
      </header>
      <div class="segs">${segs.map((s) => segmentRow(s, logoBase, noPrice)).join('')}</div>
      ${paxTable(breakdowns, paxByRef, noPrice, currency)}
      ${noPrice ? '' : totalsBlock(booking, currency)}
    </section>`;
}

const STYLE = `
  *{box-sizing:border-box}
  body{margin:0;background:#eef1f5;color:#1a2230;font:14px/1.5 -apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif}
  .wrap{max-width:820px;margin:24px auto;padding:0 16px}
  .doc-h{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px}
  .doc-h h1{font-size:20px;margin:0;letter-spacing:.5px}
  .doc-h .sub{color:#66738a;font-size:12px}
  .card{background:#fff;border-radius:14px;box-shadow:0 1px 3px rgba(20,30,50,.12);overflow:hidden;margin-bottom:20px}
  .card-h{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;background:linear-gradient(90deg,#1b3a6b,#2f6fb3);color:#fff}
  .pnr-l{font-size:11px;opacity:.8;margin-right:8px;text-transform:uppercase;letter-spacing:1px}
  .pnr{font-size:20px;font-weight:700;letter-spacing:2px}
  .chips .chip{display:inline-block;margin-left:6px;padding:3px 10px;border-radius:999px;background:rgba(255,255,255,.18);font-size:11px}
  .segs{padding:6px 20px}
  .seg{display:grid;grid-template-columns:180px 1fr 160px;gap:16px;align-items:center;padding:16px 0;border-bottom:1px dashed #d7deea}
  .seg:last-child{border-bottom:0}
  .seg-air{display:flex;align-items:center;gap:10px}
  .logo{width:34px;height:34px;object-fit:contain;border-radius:6px}
  .al-name{font-weight:600;font-size:13px}
  .al-fn{color:#66738a;font-size:12px}
  .seg-leg{display:flex;align-items:center;gap:12px}
  .ep{text-align:center;min-width:74px}
  .ep .time{font-size:19px;font-weight:700}
  .ep .code{font-size:13px;font-weight:600;color:#2f6fb3}
  .ep .city{font-size:11px;color:#66738a}
  .mid{flex:1;text-align:center;position:relative}
  .mid .line{height:2px;background:#cdd6e5;margin:14px 0 4px}
  .mid .plane{position:absolute;top:4px;left:50%;transform:translateX(-50%);color:#2f6fb3}
  .mid .dur{font-size:11px;color:#66738a}
  .seg-meta{text-align:right}
  .seg-meta .date{font-size:12px;font-weight:600}
  .tag,.seg-meta .tags .tag{display:inline-block;margin:3px 0 0 4px;padding:2px 8px;border-radius:6px;background:#eef2f8;color:#41506b;font-size:11px}
  table.pax{width:calc(100% - 40px);margin:12px 20px 4px;border-collapse:collapse;font-size:13px}
  table.pax th{text-align:left;color:#66738a;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.5px;border-bottom:2px solid #e6ebf3;padding:6px 8px}
  table.pax td{padding:8px;border-bottom:1px solid #eef2f8}
  table.pax .r{text-align:right}
  .totals{margin:8px 20px 18px;max-width:320px;margin-left:auto}
  .totals .tr{display:flex;justify-content:space-between;padding:4px 0;font-size:13px;color:#41506b}
  .totals .grand{border-top:2px solid #e6ebf3;margin-top:4px;padding-top:8px;font-weight:700;font-size:15px;color:#1a2230}
  .foot{color:#8894a8;font-size:11px;text-align:center;margin:8px 0 32px}
  @media print{body{background:#fff}.card{box-shadow:none;border:1px solid #d7deea}.wrap{margin:0;max-width:none}.doc-h{margin-bottom:8px}}
`;

/** Zengin e-bilet HTML'i — `GetShoppingCart` verisinden. */
export function buildEticketHtml(cart: ShoppingCart | null | undefined, opts: EticketOptions = {}): string {
  const logoBase = `${typeof window !== 'undefined' ? window.location.origin : ''}/img/airlines`;
  let bookings = ((cart?.airBookings ?? []) as AirBooking[]).filter((b) => (b.flightSegments?.length ?? 0) > 0);
  if (opts.pnr && !opts.grouped) bookings = bookings.filter((b) => b.bookingCode === opts.pnr);

  const paxByRef = new Map<string, Passenger>();
  (cart?.passengers ?? []).forEach((p) => {
    if (p.paxReferanceId) paxByRef.set(p.paxReferanceId, p);
  });

  const body = bookings.length
    ? bookings.map((b) => bookingCard(b, paxByRef, logoBase, opts)).join('')
    : '<section class="card"><div style="padding:24px;text-align:center;color:#66738a">Uçuş bilgisi bulunamadı.</div></section>';

  const title = opts.noPrice ? 'E-Bilet (Fiyatsız)' : 'E-Bilet';
  return `<!doctype html><html lang="tr"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${title}</title><style>${STYLE}</style></head>
<body><div class="wrap">
  <div class="doc-h"><h1>${title}</h1><div class="sub">${esc(fmtDate(new Date().toISOString()))}</div></div>
  ${body}
  <div class="foot">Bu belge elektronik olarak oluşturulmuştur. İyi yolculuklar dileriz.</div>
</div></body></html>`;
}
